import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowLeft, Mail, Users } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import SEO from "@/components/SEO";
import { capture } from "@/lib/analytics";
import { toastError } from "@/lib/toast-error";
import { useAuthProviders } from "@/hooks/useAuthProviders";
import { getAttribution, attributionPayload, hasAnyUtm } from "@/lib/attribution";

interface InvitePreview {
  email: string;
  first_name: string | null;
  last_name: string | null;
  team_name: string;
  coach_name: string;
  sport_name: string | null;
  already_used: boolean;
}

const Signup = () => {
  const { signUp } = useAuth();
  const [searchParams]    = useSearchParams();
  const inviteToken       = searchParams.get("invite") ?? "";
  const [invite, setInvite]           = useState<InvitePreview | null>(null);
  const [fullName, setFullName]       = useState("");
  const [email, setEmail]             = useState("");
  const [password, setPassword]       = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading]         = useState(false);
  const [submitted, setSubmitted]     = useState(false);
  const [inviteLoading, setInviteLoading] = useState(!!inviteToken);
  const authProviders = useAuthProviders();
  const showOAuthSection = authProviders.google || authProviders.apple;

  // Load invite preview and pre-fill fields
  useEffect(() => {
    if (!inviteToken) return;
    localStorage.setItem("vizInviteToken", inviteToken);

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
    const anonKey     = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
    fetch(`${supabaseUrl}/functions/v1/accept-team-invite?token=${inviteToken}`, {
      headers: {
        apikey: anonKey,
        // Supabase Functions gateway requires both apikey AND Authorization
        // headers; without the Bearer the request is rejected before it
        // reaches the function with "Missing authorization header".
        Authorization: `Bearer ${anonKey}`,
      },
    })
      .then(async (r) => {
        const data = await r.json().catch(() => null);
        if (!r.ok) {
          console.error("[team-invite] preview returned non-OK", r.status, data);
          return null;
        }
        return data;
      })
      .then((data: any) => {
        if (data?.email) {
          setInvite(data as InvitePreview);
          setEmail(data.email);
          const name = [data.first_name, data.last_name].filter(Boolean).join(" ");
          if (name) setFullName(name);
        } else if (data) {
          console.error("[team-invite] preview missing email field", data);
        }
      })
      .catch((err) => {
        // Logged so it's visible in DevTools and Sentry instead of silently
        // falling back to the generic "free trial" view (which misleads
        // team-invited players into thinking they need a credit card).
        console.error("[team-invite] preview fetch failed:", err);
      })
      .finally(() => setInviteLoading(false));
  }, [inviteToken]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    capture("signup_started", { method: "email" });
    const attribution = getAttribution();
    const attrPayload = attributionPayload(attribution);
    try {
      // Persist attribution onto user_metadata so it survives the email
      // verification round-trip and is readable by Subscribe.tsx /
      // Dashboard.tsx when launching checkout.
      const userMetadata = Object.keys(attrPayload).length
        ? { attribution: attrPayload }
        : undefined;
      await signUp(email, password, fullName, userMetadata);
      capture("signup_completed", { method: "email", ...attrPayload });
      if (hasAnyUtm(attribution)) {
        capture("attributed_signup", { method: "email", ...attrPayload });
      }
      setSubmitted(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : "unknown";
      capture("signup_failed", { method: "email", error: message });
      // Translate common Supabase Auth errors into actionable user messages.
      // Otherwise players see opaque copy and don't know whether to retry,
      // switch methods, or sign in instead.
      const lc = message.toLowerCase();
      if (lc.includes("rate") || lc.includes("too many") || lc.includes("over_email_send_rate_limit")) {
        toast.error("Too many sign-ups from this network. Try 'Continue with Google' instead.");
      } else if (lc.includes("already") || lc.includes("registered") || lc.includes("user_already_exists")) {
        toast.error("An account with this email already exists. Try signing in instead.");
      } else if (lc.includes("password") && lc.includes("characters")) {
        toast.error("Password must be at least 6 characters.");
      } else if (lc.includes("invalid") && lc.includes("email")) {
        toast.error("That email address looks invalid. Double-check the spelling.");
      } else {
        toastError("Couldn't create account", err, { source: "signup.email" });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = async (provider: "google" | "apple") => {
    capture("signup_started", { method: provider });
    // Land on /dashboard so SubscribedRoute can route a new OAuth
    // user to /onboarding (no profile row → onboarding_completed
    // defaults to false in AuthContext.checkOnboarding) or send
    // returning users straight through. Mirrors the email/password
    // post-signin path.
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: `${window.location.origin}/dashboard` },
    });
    if (error) {
      const providerLabel = provider.charAt(0).toUpperCase() + provider.slice(1);
      capture("signup_failed", { method: provider, error: error.message });
      toastError(`Couldn't sign up with ${providerLabel}`, error, { source: `signup.${provider}` });
    }
    // Success path is handled by the auth callback — a signup_completed event
    // will fire via AuthContext's identify flow on the next session.
  };

  if (submitted) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center gradient-hero px-4">
        <div className="w-full max-w-md">
          <div className="gradient-card rounded-2xl border border-border/50 shadow-card p-8 text-center">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Mail className="w-8 h-8 text-primary" />
            </div>
            <h2 className="font-display text-2xl font-bold mb-3">Check your email</h2>
            <p className="text-muted-foreground mb-2">
              We sent a verification link to
            </p>
            <p className="font-medium text-foreground mb-6">{email}</p>
            <p className="text-sm text-muted-foreground mb-8">
              Click the link in the email to verify your account, then come back here to sign in. It may take a minute to arrive — check your spam folder too.
            </p>
            <Link to="/login">
              <Button variant="hero" size="lg" className="w-full">
                Go to Sign In
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] flex items-center justify-center gradient-hero px-4">
      <SEO
        title="Create your VIZ account — Free 7-day trial"
        description="Start your free trial of VIZ. AI-powered personalized visualization training for athletes."
        path="/signup"
      />
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>

        <div className="gradient-card rounded-2xl border border-border/50 shadow-card p-8">
          <div className="text-center mb-8">
            <h1 className="font-display text-3xl font-bold mb-2">
              <span className="text-gradient-primary">VIZ</span>
            </h1>
            {invite ? (
              <div className="mt-3 px-4 py-3 rounded-xl bg-primary/10 border border-primary/20 text-left">
                <div className="flex items-start gap-2">
                  <Users className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-foreground">{invite.team_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {invite.coach_name} invited you · {invite.sport_name ?? ""}
                    </p>
                  </div>
                </div>
              </div>
            ) : inviteLoading ? (
              <p className="text-muted-foreground text-sm mt-2">Loading your invite…</p>
            ) : inviteToken ? (
              // Fallback when an invite token is in the URL but the preview
              // fetch failed for any reason. We still show team-friendly copy
              // so players don't see misleading "7-day free trial" messaging
              // that suggests they need to enter a credit card.
              <div className="mt-3 px-4 py-3 rounded-xl bg-primary/10 border border-primary/20 text-left">
                <div className="flex items-start gap-2">
                  <Users className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-foreground">You're joining a team</p>
                    <p className="text-xs text-muted-foreground">
                      Sign up with the email your coach used to invite you. No credit card required.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground">Start your 7-day free trial.</p>
            )}
          </div>

          {/* Social login buttons — only render when the provider is actually
              enabled in the Supabase project. Otherwise clicking would call
              /authorize which returns 400 "provider is not enabled" and
              players see "Couldn't sign up with Google" — exactly what
              blocked players during the 2026-05-05 pilot. */}
          {showOAuthSection && (
            <>
              <div className="space-y-3 mb-6">
                {authProviders.google && (
                  <Button
                    variant="outline"
                    size="lg"
                    className="w-full bg-secondary/30 border-border/50 hover:bg-secondary/60"
                    onClick={() => handleSocialLogin("google")}
                    type="button"
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    Continue with Google
                  </Button>
                )}
                {authProviders.apple && (
                  <Button
                    variant="outline"
                    size="lg"
                    className="w-full bg-secondary/30 border-border/50 hover:bg-secondary/60"
                    onClick={() => handleSocialLogin("apple")}
                    type="button"
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                    </svg>
                    Continue with Apple
                  </Button>
                )}
              </div>

              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border/50" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-card px-3 text-muted-foreground">or sign up with email</span>
                </div>
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="Your name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                className="bg-secondary/50 border-border/50"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-secondary/50 border-border/50"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="At least 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="bg-secondary/50 border-border/50 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button variant="hero" size="lg" className="w-full" type="submit" disabled={loading}>
              {loading ? "Creating account..." : inviteToken ? "Claim your sessions" : "Start Free Trial"}
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              {inviteToken
                ? "No subscription needed · Team credits applied automatically"
                : "7-day free trial · No commitment · Cancel anytime"}
            </p>
            <p className="text-xs text-center text-muted-foreground">
              By signing up, you agree to our{" "}
              <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>
              {" "}and{" "}
              <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>.
            </p>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Already have an account?{" "}
            <Link to="/login" className="text-primary hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;